import React, {useEffect, useState} from "react";
import {z} from "zod";
import {styled} from "@mui/material/styles";
import LoadingButton from '@mui/lab/LoadingButton';

import InputContainer from "@/components/form/InputContainer.jsx";
import Input from '@/components/form/MUIInput.jsx';
import Select from '@/components/form/MUISelect.jsx';
import Form from "@/components/form/Form.jsx";
import {useForm} from "@/hooks/useForm.jsx";
import {useFormProgress} from "@/hooks/useFormProgress.jsx";
import ContainerBox from "@/components/ContainerBox.jsx";
import {
    ContainerBoxHeader,
    ContainerBoxHeaderRight,
    ContainerBoxSkipLink,
    ContainerBoxTitle
} from "@/components/typography/index.jsx";
import {SIGN_UP_EDUCATIONAL_BACKGROUND_ENDPOINT} from "@/constants/endpoints.jsx";
import LocationSearchInput from "@/components/form/LocationSearchInput.jsx";
import {STUDENT_COMPLETE_PROFILE} from "@/constants/routes/sign-up.jsx";
import {useLocalStorage} from "@/hooks/useLocalStorage.jsx";
import {usePreFetchData} from "@/hooks/usePreFetchData.jsx";
import YearMask from "@/components/form/masks/YearMask.jsx";

const schema = z.object({
    education_level: z.string().optional(),
    college: z.string().optional(),
    college_id: z.number().optional(),
    graduation_year: z.string().optional(),
    major: z.string().optional(),
});

export default function StudentEducationalBackground({navigateOnSubmitTo = STUDENT_COMPLETE_PROFILE}) {
    const {navigate, setCurrentProgress} = useFormProgress();
    const {storeData, getStoredData} = useLocalStorage()
    const {preFetchedData} = usePreFetchData()

    const [educationOptions, setEducationOptions] = useState([]);

    const {
        register,
        onSubmit,
        getValues,
        setValue,
        state,
        isValid,
        isLoading,
    } = useForm({
        endpoint: SIGN_UP_EDUCATIONAL_BACKGROUND_ENDPOINT,
        schema,
        defaultValues: {
            education_level: '',
            college: '',
            college_id: 0,
            graduation_year: '',
            major: '',
        }
    });

    useEffect(() => {
        setCurrentProgress(2);
    }, []);

    useEffect(() => {
        if(preFetchedData?.education_options){
            setEducationOptions(
                preFetchedData.education_options.map((level) => ({
                    value: level,
                    label: level,
                }))
            );
        }
    }, [preFetchedData]);


    useEffect(() => {
        const storedData = getStoredData();

        setValue('college_id', storedData?.college_id ?? 0);
        setValue('college', storedData?.college ?? '');
    }, [getStoredData]);

    const handleSubmit = (endpointResponse, data) => {
        navigate(navigateOnSubmitTo);
    }

    return (
        <>
            <ContainerBox>
                <ContainerBoxHeader>
                    <ContainerBoxTitle>Educational Background</ContainerBoxTitle>
                    <ContainerBoxHeaderRight>
                        <ContainerBoxSkipLink onClick={() => {
                            navigate(navigateOnSubmitTo);
                        }}>Skip</ContainerBoxSkipLink>
                    </ContainerBoxHeaderRight>
                </ContainerBoxHeader>

                <Form onSubmit={onSubmit(handleSubmit)} allowSubmitOnEnter={false}>
                    <InputContainer>
                        <Select
                            label={"Highest Level of Education"}
                            placeholder={"Select Education"}
                            size={'md'}
                            value={getValues("education_level")}
                            {...register("education_level")}
                            errorMessage={state?.errors?.education_level?.message ?? ''}
                            options={educationOptions}
                        />
                    </InputContainer>

                    <LocationSearchInputContainer>
                        <LocationSearchInput
                            label={"Select Your College"}
                            placeholder={"Select Your College"}
                            size={'md'}
                            searchType={'colleges'}
                            value={getValues('college')}
                            initLocation={{
                                id: getValues('college_id') ?? 0,
                                name: getValues('college') ?? '',
                            }}
                            onChange={(new_location, new_location_name) => {
                                setValue('college_id', new_location?.id ?? 0);
                                storeData('college_id', new_location?.id ?? 0);

                                setValue('college', new_location_name ?? '');
                                storeData('college', new_location_name ?? '');
                            }}
                        />
                    </LocationSearchInputContainer>

                    <InputContainer>
                        <Input
                            label={"College Graduation Year"}
                            placeholder={"College Graduation Year"}
                            size={'md'}
                            inputComponent={YearMask}
                            {...register("graduation_year")}
                            errorMessage={state?.errors?.graduation_year?.message ?? ''}
                        />
                    </InputContainer>

                    <InputContainer>
                        <Input
                            label={"Major/Field of Study"}
                            placeholder={"Major/Field of Study"}
                            size={'md'}
                            {...register("major")}
                            errorMessage={state?.errors?.major?.message ?? ''}
                        />
                    </InputContainer>

                    <LoadingButton
                        loading={isLoading}
                        disabled={!isValid}
                        variant="contained"
                        type="submit"
                        size="medium"
                    >
                        Continue →
                    </LoadingButton>
                </Form>
            </ContainerBox>
        </>
    );
};

const LocationSearchInputContainer = styled(InputContainer)({
    position: 'relative',
    width: '350px',
    maxWidth: '100%',
});
